import { z } from 'zod';
import { addressSchema, emailSchema, phoneSchema } from '@/shared/validation/common.schemas';

/**
 * Order Form Validation Schema (Admin Edit Dialog)
 *
 * This schema is for editing existing waste collection orders.
 * Differences from wasteCollectionSchema:
 * - Date is read-only (no validation needed)
 * - Photos are read-only (no validation needed)
 * - Consent is always true (no validation needed)
 * - Includes id and schoolId for editing
 */

// Helper: Positive number or empty (for waste quantities)
const positiveNumberOrEmpty = z.coerce.number().min(0, 'Valoarea trebuie să fie pozitivă').optional();

/**
 * Order form schema for editing existing orders
 */
export const orderFormSchema = z.object({
  // IDs for editing
  id: z.string().optional(),
  schoolId: z.string().optional(),

  // Contact Information (Required)
  contactPersonName: z.string().min(1, 'Numele persoanei de contact este obligatoriu'),
  contactPersonEmail: emailSchema,
  contactPersonPhone: phoneSchema,

  // Pickup Date (Read-only, no validation)
  dataOrientativaColectare: z.union([z.number(), z.date()]).optional(),

  // Address Toggle (Required)
  pickupAddressSameWithSchoolAddress: z.boolean({
    required_error: 'Vă rugăm să specificați dacă adresa de colectare este aceeași cu adresa școlii',
  }),

  // Pickup Address (Required, nested object)
  pickupAddress: addressSchema,

  // Waste Quantities (Optional, automatic number coercion)
  deeeKg: positiveNumberOrEmpty,
  dbaKg: positiveNumberOrEmpty,
  becuriSiNeoaneKg: positiveNumberOrEmpty,

  // Photos (Read-only, no validation)
  photos: z.array(z.string()).optional(),

  // Consent (Always true in edit mode, no validation)
  consent: z.boolean().optional(),
});

/**
 * Inferred TypeScript type from schema
 */
export type OrderFormData = z.infer<typeof orderFormSchema>;
